import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useAlert } from '../contexts/AlertContext'
import Topbar from '../components/layout/Topbar'
import Sidebar from '../components/layout/Sidebar'
import { PieChart, LineChart, BarChart } from '../components/charts'
import MapList from '../components/MapList'
import MapUpload from '../components/MapUpload'
import MapEditor from '../components/MapEditor'
import config from '../config'

const API = config.API_BASE

export default function SuperAdminPage() {
  const { user, logout, token } = useAuth()
  const { alert } = useAlert()
  const [tab, setTab] = useState('dashboard') // 'dashboard' | 'tenants' | 'crm'
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState(null)
  const [tenants, setTenants] = useState([])
  const [tenantQuery, setTenantQuery] = useState('')
  const [tenantName, setTenantName] = useState('')
  const [users, setUsers] = useState([])
  const [userForm, setUserForm] = useState({ email: '', password: '', role: 'ADMIN', tenantId: '' })
  const [userFilterTenant, setUserFilterTenant] = useState('')

  // CRM Configuration state
  const [selectedTenantForCRM, setSelectedTenantForCRM] = useState(null)
  const [crmConfig, setCrmConfig] = useState({
    crmEnabled: false,
    crmType: 'zoho',
    crmConfig: {
      clientId: '',
      clientSecret: '',
      refreshToken: '',
      apiDomain: 'https://www.zohoapis.com',
      accountsDomain: 'https://accounts.zoho.com'
    }
  })
  const [crmLoading, setCrmLoading] = useState(false)
  const [crmMessage, setCrmMessage] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isSmall, setIsSmall] = useState(false)
  const [chartData, setChartData] = useState({
    systemOverview: { labels: [], datasets: [] },
    growthTrends: { labels: [], datasets: [] }
  })
  const [chartsLoading, setChartsLoading] = useState(false)
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token])

  // Plans drilldown state (tenant → maps)
  const [selectedTenantId, setSelectedTenantId] = useState('')
  const [plansMaps, setPlansMaps] = useState([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [editModal, setEditModal] = useState(null) // { map }

  const isError = crmMessage.toLowerCase().includes('fail');

  const fetchTenantMaps = async (tenantId) => {
    setPlansLoading(true)
    try {
      // First fetch lightweight list of maps by tenant
      const res = await fetch(`${API}/super/tenants/${tenantId}/maps`, { headers: authHeaders })
      if (!res.ok) { setPlansMaps([]); return }
      const base = await res.json()
      // Then fetch detailed payloads in parallel to match Admin's MapList expectations
      const detailed = await Promise.all(base.map(async (m) => {
        const mr = await fetch(`${API}/super/maps/${m.id}`, { headers: authHeaders })
        return mr.ok ? await mr.json() : m
      }))
      setPlansMaps(detailed)
    } finally {
      setPlansLoading(false)
    }
  }

  const fetchChartData = async () => {
    if (!token) return

    setChartsLoading(true)
    try {
      const [systemOverviewRes, growthTrendsRes] = await Promise.all([
        fetch(`${API}/charts/system-overview`, { headers: authHeaders }),
        fetch(`${API}/charts/growth-trends?days=30`, { headers: authHeaders })
      ])

      const [systemOverview, growthTrends] = await Promise.all([
        systemOverviewRes.ok ? systemOverviewRes.json() : { labels: [], datasets: [] },
        growthTrendsRes.ok ? growthTrendsRes.json() : { labels: [], datasets: [] }
      ])

      setChartData({
        systemOverview,
        growthTrends
      })
    } catch (err) {
      console.error('Failed to fetch chart data:', err)
    } finally {
      setChartsLoading(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [ov, ts, us] = await Promise.all([
          fetch(`${API}/super/overview`, { headers: authHeaders }).then(r => r.json()),
          fetch(`${API}/super/tenants`, { headers: authHeaders }).then(r => r.json()),
          fetch(`${API}/super/users`, { headers: authHeaders }).then(r => r.json())
        ])
        setOverview(ov)
        setTenants(Array.isArray(ts) ? ts : [])
        setUsers(Array.isArray(us) ? us : [])
      } catch (_) { /* ignore */ }
      finally { setLoading(false) }
    }
    if (token) {
      fetchData()
      fetchChartData()
    }
  }, [token])

  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth <= 992)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Set document title based on user role
  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN') {
      document.title = 'Super Admin'
    } else {
      document.title = 'Super Admin'
    }
    return () => {
      document.title = 'React App'
    }
  }, [user?.role])

  const createTenant = async (e) => {
    e.preventDefault()
    if (!tenantName.trim()) return
    try {
      const res = await fetch(`${API}/super/tenants`, { method: 'POST', headers: authHeaders, body: JSON.stringify({ name: tenantName.trim() }) })
      if (!res.ok) {
        const txt = await res.text().catch(() => '')
        alert.error(`Create tenant failed (${res.status}). ${txt}`)
        return
      }
      const t = await res.json()
      setTenants([t, ...tenants])
      setTenantName('')
    } catch (e) { alert.error('Create tenant error') }
  }

  const renameTenant = async (id, name) => {
    const res = await fetch(`${API}/super/tenants/${id}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ name }) })
    if (res.ok) {
      const t = await res.json()
      setTenants(tenants.map(x => x.id === id ? { ...x, name: t.name } : x))
    }
  }

  const deleteTenant = async (id) => {
    alert.confirm(
      'Delete this tenant? Must have no users or maps.',
      'Delete Tenant',
      () => {
        fetch(`${API}/super/tenants/${id}`, { method: 'DELETE', headers: authHeaders })
          .then(async (res) => {
            if (!res.ok) {
              const j = await res.json().catch(() => ({}))
              alert.error(j.error || 'Delete failed')
              return
            }
            setTenants(tenants.filter(t => t.id !== id))
          })
          .catch(() => {
            alert.error('Delete failed')
          })
      }
    )
  }

  const refreshUsers = async (tenantId) => {
    const q = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''
    const res = await fetch(`${API}/super/users${q}`, { headers: authHeaders })
    if (res.ok) setUsers(await res.json())
  }

  const createUser = async (e) => {
    e.preventDefault()
    if (!userForm.email || !userForm.password || !userForm.tenantId) {
      alert.error('Email, password and tenant are required')
      return
    }
    const res = await fetch(`${API}/super/users`, { method: 'POST', headers: authHeaders, body: JSON.stringify(userForm) })
    if (!res.ok) { alert.error('Create user failed'); return }
    setUserForm({ email: '', password: '', role: 'ADMIN', tenantId: userForm.tenantId })
    refreshUsers(userFilterTenant)
  }

  const updateUser = async (id, changes) => {
    const res = await fetch(`${API}/super/users/${id}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(changes) })
    if (res.ok) refreshUsers(userFilterTenant)
  }

  const deleteUser = async (id) => {
    alert.confirm(
      'Delete this user?',
      'Delete User',
      () => {
        fetch(`${API}/super/users/${id}`, { method: 'DELETE', headers: authHeaders })
          .then(async (res) => {
            if (res.ok) {
              refreshUsers(userFilterTenant);
              return
            }
            if (res.status === 409) {
              const data = await res.json().catch(() => ({}))
              const t = data?.details?.totalVersions ?? 0
              const p = data?.details?.publishedVersions ?? 0
              alert.error(`Cannot delete user: they own versions.\nPublished: ${p}, Total: ${t}.\nTransfer ownership or unpublish, then try again.`)
              return
            }
            const txt = await res.text().catch(() => '')
            alert.error(`Delete failed (${res.status}). ${txt}`)
          })
          .catch(() => {
            alert.error('Delete failed')
          })
      }
    )
  }

  // CRM Configuration functions
  const loadTenantCRM = async (tenantId) => {
    try {
      const response = await fetch(`${API}/super/tenants/${tenantId}/crm`, { headers: authHeaders })
      if (response.ok) {
        const data = await response.json()
        setCrmConfig({
          crmEnabled: data.crmEnabled || false,
          crmType: data.crmType || 'zoho',
          crmConfig: data.crmConfig || {
            clientId: '',
            clientSecret: '',
            refreshToken: '',
            apiDomain: 'https://www.zohoapis.com',
            accountsDomain: 'https://accounts.zoho.com'
          }
        })
      }
    } catch (error) {
      console.error('Error loading tenant CRM:', error)
    }
  }

  const saveCRMConfig = async () => {
    if (!selectedTenantForCRM) return

    setCrmLoading(true)
    setCrmMessage('')
    try {
      const response = await fetch(`${API}/super/tenants/${selectedTenantForCRM.id}/crm`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(crmConfig)
      })

      const result = await response.json()

      if (response.ok) {
        setCrmMessage('CRM configuration saved successfully!')
        loadTenantCRM(selectedTenantForCRM.id)
      } else {
        setCrmMessage(`Error: ${result.error}`)
      }
    } catch (error) {
      setCrmMessage(`Error: ${error.message}`)
    } finally {
      setCrmLoading(false)
    }
  }

  const testCRMConnection = async () => {
    if (!selectedTenantForCRM) return

    setCrmLoading(true)
    setCrmMessage('')
    try {
      const response = await fetch(`${API}/super/tenants/${selectedTenantForCRM.id}/crm/test`, {
        method: 'POST',
        headers: authHeaders
      })

      const result = await response.json()
      setCrmMessage(result.success ? 'CRM connection successful!' : `Connection failed: ${result.error}`)
    } catch (error) {
      setCrmMessage(`Test failed: ${error.message}`)
    } finally {
      setCrmLoading(false)
    }
  }

  return (
    <div className="app-bg" style={{ minHeight: '100vh', padding: '16px 16px 16px 0' }}>
      <Topbar
        title="Super Admin"
        onMenu={() => setDrawerOpen(true)}
        onLogout={logout}
        userEmail={user?.email}
        userRole={user?.role || 'SUPER_ADMIN'}
        onSettingsClick={() => setTab('settings')}
      />
      {isSmall && drawerOpen && (
        <>
          <div className="drawer-overlay" onClick={() => setDrawerOpen(false)}></div>
          <div className={`drawer glass open`} style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="row-between" style={{ marginBottom: 8 }}>
              <div className="title">Menu</div>
              <button className="btn btn-ghost btn-sm" aria-label="Close menu" onClick={() => setDrawerOpen(false)}>✕</button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{user?.email}</div>
            <button className={`btn sidebar-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => { setTab('dashboard'); setDrawerOpen(false) }}>Dashboard</button>
            <button className={`btn sidebar-btn ${tab === 'plans' ? 'active' : ''}`} onClick={() => { setTab('plans'); setDrawerOpen(false) }}>Plans</button>
            <button className={`btn sidebar-btn ${tab === 'tenants' ? 'active' : ''}`} onClick={() => { setTab('tenants'); setDrawerOpen(false) }}>Tenants</button>
            <button className={`btn sidebar-btn ${tab === 'users' ? 'active' : ''}`} onClick={() => { setTab('users'); setDrawerOpen(false) }}>Users</button>
            <button className={`btn sidebar-btn ${tab === 'crm' ? 'active' : ''}`} onClick={() => { setTab('crm'); setDrawerOpen(false) }}>CRM Config</button>
            <button className={`btn sidebar-btn ${tab === 'settings' ? 'active' : ''}`} onClick={() => { setTab('settings'); setDrawerOpen(false) }}>Settings</button>
          </div>
        </>
      )}
      <div className="layout" style={{ marginTop: 12 }}>
        {!isSmall && (
          <div className="sidebar" style={{ padding: 0, display: 'flex', flexDirection: 'column', gap: 8, borderRight: '1px solid #e5e7eb' }}>
            <Sidebar bare active={tab} variant="superadmin" onSelect={(key) => {
              if (key === 'dashboard') setTab('dashboard')
              if (key === 'plans') setTab('plans')
              if (key === 'tenants') setTab('tenants')
              if (key === 'users') setTab('users')
              if (key === 'crm') setTab('crm')
              if (key === 'settings') setTab('settings')
            }} />
          </div>
        )}

        <div className="content">
          {loading && (
            <>
              <div className="glass glass-card" style={{ marginBottom: 12 }}>
                <div className="row-between" style={{ marginBottom: 12 }}>
                  <div className="skeleton skeleton-line tall" style={{ width: 180 }}></div>
                  <div className="skeleton skeleton-line" style={{ width: 120 }}></div>
                </div>
                <div className="skeleton-grid">
                  <div className="skeleton-card"><div className="skeleton skeleton-line" style={{ width: 80 }}></div><div className="skeleton skeleton-line tall" style={{ width: 60 }}></div></div>
                  <div className="skeleton-card"><div className="skeleton skeleton-line" style={{ width: 90 }}></div><div className="skeleton skeleton-line tall" style={{ width: 70 }}></div></div>
                  <div className="skeleton-card"><div className="skeleton skeleton-line" style={{ width: 110 }}></div><div className="skeleton skeleton-line tall" style={{ width: 50 }}></div></div>
                </div>
              </div>
              <div className="glass glass-card" style={{ marginBottom: 12 }}>
                <div className="skeleton skeleton-line tall" style={{ width: 200, marginBottom: 12 }}></div>
                <div className="skeleton" style={{ height: 320, borderRadius: 12 }}></div>
              </div>
            </>
          )}

          {!loading && tab === 'dashboard' && (
            <>
              <div className="glass-warm" style={{ padding: 14, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <div className="title">Welcome, {user?.email?.split('@')[0] || 'Super Admin'}</div>
                  <div className="muted" style={{ fontSize: 12 }}>Manage tenants, users, maps and published versions</div>
                </div>
                <div className="row gap-8">
                  <button className="btn btn-primary" onClick={() => setTab('tenants')}>View Tenants</button>
                  <button className="btn" onClick={() => setTab('users')}>Manage Users</button>
                </div>
              </div>

              <div className="glass-card" style={{ display: 'grid', gap: 12 }}>
                <div className="row-between">
                  <h3 className="title">Overview</h3>
                  <span className="muted" style={{ fontSize: 12 }}>Current month</span>
                </div>
                <div className="stat-grid">
                  <div className="stat-card stat-card-red">
                    {/* <div className="stat-icon">🏢</div> */}
                    <div className="stat-title">Tenants</div>
                    <div className="stat-value">{overview?.tenants ?? 0}</div>
                  </div>
                  <div className="stat-card stat-card-green">
                    {/* <div className="stat-icon">👥</div> */}
                    <div className="stat-title">Users</div>
                    <div className="stat-value">{overview?.users ?? 0}</div>
                  </div>
                  <div className="stat-card stat-card-blue">
                    {/* <div className="stat-icon">📈</div> */}
                    <div className="stat-title">Published</div>
                    <div className="stat-value">{overview?.published ?? 0}</div>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div style={{ display: 'grid', gap: 16 }}>
                <div className="chart-section-header">
                  <h3 className="chart-section-title">System Analytics</h3>
                  <button
                    className="chart-refresh-btn"
                    onClick={fetchChartData}
                    disabled={chartsLoading}
                  >
                    {chartsLoading ? 'Loading...' : 'Refresh Charts'}
                  </button>
                </div>

                <div className="charts-grid-large">
                  {/* System Overview */}
                  <div className="glass glass-card">
                    <BarChart
                      data={chartData.systemOverview}
                      title="Tenant Activity Overview"
                      height={380}
                      colors={['#2563eb', '#059669']}
                      loading={chartsLoading}
                    />
                  </div>

                  {/* Growth Trends */}
                  <div className="glass glass-card">
                    <LineChart
                      data={{
                        labels: chartData.growthTrends.labels,
                        values: chartData.growthTrends.datasets?.[0]?.data || [],
                        datasetLabel: 'New Tenants'
                      }}
                      title="Growth Trends (30 days)"
                      height={380}
                      color="#059669"
                      showArea={true}
                      loading={chartsLoading}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {!loading && tab === 'tenants' && (
            <div className="glass-card">
              <div className="row-between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h3 className="title">Tenants</h3>
                <div className="row gap-8" style={{ alignItems: 'center' }}>
                  <input className="input" placeholder="Search tenants" value={tenantQuery} onChange={(e) => setTenantQuery(e.target.value)} style={{ width: 'min(100%, 360px)' }} />
                  <button className="btn btn-primary" onClick={() => setTab('tenant-create')}>Create Tenant</button>
                </div>
              </div>
              <div className="map-list-grid">
                {tenants.filter(t => t.name.toLowerCase().includes(tenantQuery.toLowerCase())).length === 0 ? (
                  <div className="muted" style={{ padding: 12 }}>No results found</div>
                ) : tenants.filter(t => t.name.toLowerCase().includes(tenantQuery.toLowerCase())).map(t => (
                  <div key={t.id} className="map-card tile3d tile-pad" style={{ cursor: 'pointer', marginBottom: 12 }} onClick={() => { setTab('users'); setUserFilterTenant(t.id); refreshUsers(t.id) }}>
                    <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h4 className="card-title" style={{ margin: 0 }}>{t.name}</h4>
                    </div>
                    <div className="status-summary">
                      <div className="status-badge"><div style={{ background: '#9bc7ec' }}></div><span>Users: {t._count?.users || 0}</span></div>
                      <div className="status-badge"><div style={{ background: '#f6a950' }}></div><span>Maps: {t._count?.maps || 0}</span></div>
                    </div>
                    <div className="row gap-8" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); const name = prompt('Rename tenant', t.name); if (name) renameTenant(t.id, name) }}>Rename</button>
                      <button className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); deleteTenant(t.id) }}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && tab === 'plans' && (
            <div className="glass-card" style={{ display: 'grid', gap: 16 }}>
              <div className="row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                <h3 className="title">Plans</h3>
                <div className="row gap-8">
                  <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>Upload Map</button>
                  {selectedTenantId && (
                    <button className="btn" onClick={() => { setSelectedTenantId(''); setPlansMaps([]) }}>Back to Tenants</button>
                  )}
                </div>
              </div>

              {!selectedTenantId && (
                <div className="map-list-grid">
                  {tenants.length === 0 ? (
                    <div className="muted" style={{ padding: 12 }}>No tenants found</div>
                  ) : tenants.map(t => (
                    <div key={t.id} className="map-card tile3d tile-pad" style={{ cursor: 'pointer', marginBottom: 12 }} onClick={() => { setSelectedTenantId(t.id); fetchTenantMaps(t.id) }}>
                      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h4 className="card-title" style={{ margin: 0 }}>{t.name}</h4>
                      </div>
                      <div className="status-summary">
                        <div className="status-badge"><div style={{ background: '#9bc7ec' }}></div><span>Users: {t._count?.users || 0}</span></div>
                        <div className="status-badge"><div style={{ background: '#f6a950' }}></div><span>Maps: {t._count?.maps || 0}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedTenantId && (
                plansLoading ? (
                  <div className="loading-spinner">Loading maps…</div>
                ) : (
                  plansMaps.length === 0 ? (
                    <div className="glass-card" style={{ padding: 12 }}>No maps for this tenant</div>
                  ) : (
                    <MapList
                      maps={plansMaps}
                      onMapSelect={(map) => setEditModal({ map })}
                      onRefresh={() => fetchTenantMaps(selectedTenantId)}
                    />
                  )
                )
              )}

              {/* Modal moved outside of glass-card to avoid clipping/stacking issues */}
            </div>
          )}

          {!loading && tab === 'tenant-create' && (
            <div className="glass-card" style={{ display: 'grid', gap: 12, maxWidth: 520 }}>
              <h3 className="title">Create Tenant</h3>
              <form onSubmit={createTenant} className="row gap-8" style={{ alignItems: 'center' }}>
                <input value={tenantName} onChange={(e) => setTenantName(e.target.value)} placeholder="Tenant name" className="input" />
                <button className="btn btn-primary" type="submit">Create</button>
              </form>
              <div className="muted" style={{ fontSize: 12 }}>Tip: After creating a tenant, go to Users to invite admins.</div>
            </div>
          )}

          {!loading && tab === 'users' && (
            <div className="glass-card" style={{ display: 'grid', gap: 16 }}>
              <div className="row-between" style={{ gap: 12 }}>
                <h3 className="title">Users</h3>
                <div className="row gap-8">
                  <select className="select" value={userFilterTenant} onChange={(e) => { setUserFilterTenant(e.target.value); refreshUsers(e.target.value) }}>
                    <option value="">All tenants</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>

              <form className="row gap-8" onSubmit={createUser} style={{ flexWrap: 'wrap' }} autoComplete="off">
                <input className="input" type="email" name="sa_user_email" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} placeholder="Email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                <input className="input" placeholder="Password" type="password" name="sa_user_password" autoComplete="new-password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
                <select className="select" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                  <option value="ADMIN">ADMIN</option>
                  <option value="VIEWER" disabled>VIEWER (reserved)</option>
                </select>
                <select className="select" value={userForm.tenantId} onChange={(e) => setUserForm({ ...userForm, tenantId: e.target.value })}>
                  <option value="">Select tenant</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <button className="btn btn-primary" type="submit">Create User</button>
              </form>

              {users.length === 0 ? (
                <div className="glass-card" style={{ padding: 12 }}>No results found</div>
              ) : (
                <div className="glass-card" style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}>
                        <th style={{ padding: '8px 6px' }}>Email</th>
                        <th style={{ padding: '8px 6px' }}>Role</th>
                        <th style={{ padding: '8px 6px' }}>Tenant</th>
                        <th style={{ padding: '8px 6px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id} style={{ borderTop: '1px solid #eef2f7' }}>
                          <td style={{ padding: '8px 6px' }}>{u.email}</td>
                          <td style={{ padding: '8px 6px' }}>
                            <select className="select" value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value, tenantId: u.tenantId })}>
                              <option value="ADMIN">ADMIN</option>
                              <option value="SUPER_ADMIN" disabled>SUPER_ADMIN</option>
                            </select>
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            {/* <select className="select" value={u.tenantId || ''} onChange={(e)=>updateUser(u.id, { role: u.role, tenantId: e.target.value })}>
                          {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select> */}
                            <span>{tenants.find(t => t.id === u.tenantId)?.name || '-'}</span>
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u.id)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {!loading && tab === 'crm' && (
            <div className="glass-card" style={{ display: 'grid', gap: 16 }}>
              <h3 className="title">CRM Configuration</h3>

              {/* Tenant Selection */}
              <div style={{ display: 'grid', gap: 8 }}>
                <label style={{ fontWeight: 'bold' }}>Select Tenant</label>
                <select
                  className="select"
                  value={selectedTenantForCRM?.id || ''}
                  onChange={(e) => {
                    const tenant = tenants.find(t => t.id === e.target.value)
                    setSelectedTenantForCRM(tenant)
                    if (tenant) loadTenantCRM(tenant.id)
                  }}
                >
                  <option value="">Choose a tenant...</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedTenantForCRM && (
                <div style={{ display: 'grid', gap: 16, padding: 16, background: '#f9f9f9', borderRadius: 8, border: '1px solid #ddd' }}>
                  <h4 style={{ margin: 0 }}>CRM Configuration for {selectedTenantForCRM.name}</h4>

                  {/* CRM Enable/Disable */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="crmEnabled"
                      checked={crmConfig.crmEnabled}
                      onChange={(e) => setCrmConfig({
                        ...crmConfig,
                        crmEnabled: e.target.checked
                      })}
                    />
                    <label htmlFor="crmEnabled" style={{ margin: 0 }}>Enable CRM Integration</label>
                  </div>

                  {crmConfig.crmEnabled && (
                    <>
                      {/* CRM Type */}
                      <div style={{ display: 'grid', gap: 8 }}>
                        <label style={{ fontWeight: 'bold' }}>CRM Type</label>
                        <select
                          className="select"
                          value={crmConfig.crmType}
                          onChange={(e) => setCrmConfig({
                            ...crmConfig,
                            crmType: e.target.value
                          })}
                        >
                          <option value="zoho">Zoho CRM</option>
                          <option value="salesforce">Salesforce</option>
                          <option value="hubspot">HubSpot</option>
                        </select>
                      </div>

                      {/* Zoho CRM Configuration */}
                      {crmConfig.crmType === 'zoho' && (
                        <div style={{ display: 'grid', gap: 16, padding: 16, background: 'white', borderRadius: 6, border: '1px solid #eee' }}>
                          <h5 style={{ margin: 0 }}>Zoho CRM Credentials</h5>

                          <div style={{ display: 'grid', gap: 8 }}>
                            <label style={{ fontWeight: 'bold' }}>Client ID</label>
                            <input
                              className="input"
                              type="text"
                              value={crmConfig.crmConfig.clientId}
                              onChange={(e) => setCrmConfig({
                                ...crmConfig,
                                crmConfig: {
                                  ...crmConfig.crmConfig,
                                  clientId: e.target.value
                                }
                              })}
                              placeholder="1000.ABC123DEF456GHI789..."
                            />
                          </div>

                          <div style={{ display: 'grid', gap: 8 }}>
                            <label style={{ fontWeight: 'bold' }}>Client Secret</label>
                            <input
                              className="input"
                              type="password"
                              value={crmConfig.crmConfig.clientSecret}
                              onChange={(e) => setCrmConfig({
                                ...crmConfig,
                                crmConfig: {
                                  ...crmConfig.crmConfig,
                                  clientSecret: e.target.value
                                }
                              })}
                              placeholder="a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0"
                            />
                          </div>

                          <div style={{ display: 'grid', gap: 8 }}>
                            <label style={{ fontWeight: 'bold' }}>Refresh Token</label>
                            <input
                              className="input"
                              type="password"
                              value={crmConfig.crmConfig.refreshToken}
                              onChange={(e) => setCrmConfig({
                                ...crmConfig,
                                crmConfig: {
                                  ...crmConfig.crmConfig,
                                  refreshToken: e.target.value
                                }
                              })}
                              placeholder="1000.xyz123abc456def789ghi012jkl345mno678pqr901"
                            />
                          </div>

                          <div style={{ display: 'grid', gap: 8 }}>
                            <label style={{ fontWeight: 'bold' }}>API Domain</label>
                            <input
                              className="input"
                              type="text"
                              value={crmConfig.crmConfig.apiDomain}
                              onChange={(e) => setCrmConfig({
                                ...crmConfig,
                                crmConfig: {
                                  ...crmConfig.crmConfig,
                                  apiDomain: e.target.value
                                }
                              })}
                              placeholder="https://www.zohoapis.com"
                            />
                          </div>

                          <div style={{ display: 'grid', gap: 8 }}>
                            <label style={{ fontWeight: 'bold' }}>Accounts Domain</label>
                            <input
                              className="input"
                              type="text"
                              value={crmConfig.crmConfig.accountsDomain}
                              onChange={(e) => setCrmConfig({
                                ...crmConfig,
                                crmConfig: {
                                  ...crmConfig.crmConfig,
                                  accountsDomain: e.target.value
                                }
                              })}
                              placeholder="https://accounts.zoho.com"
                            />
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-primary"
                          onClick={saveCRMConfig}
                          disabled={crmLoading}
                        >
                          {crmLoading ? 'Saving...' : 'Save Configuration'}
                        </button>

                        <button
                          className="btn"
                          onClick={testCRMConnection}
                          disabled={crmLoading || !crmConfig.crmEnabled}
                        >
                          {crmLoading ? 'Testing...' : 'Test Connection'}
                        </button>
                      </div>
                    </>
                  )}

                  {/* Status Message */}
                  {crmMessage && (
                    <div style={{
                      padding: 12,
                      borderRadius: 4,
                      background: isError ? '#f8d7da' : '#d4edda',
                      color: isError ? '#721c24' : '#155724',
                      border: `1px solid ${isError ? '#f5c6cb' : '#c3e6cb'}`
                    }}>
                      {crmMessage}
                    </div>
                  )}

                </div>
              )}
            </div>
          )}

          {tab === 'settings' && (
            <div className="glass-card">
              <h3 className="title" style={{ marginBottom: 8 }}>Settings</h3>
              <div className="muted">Settings placeholders (branding, limits) — to be implemented.</div>
            </div>
          )}
        </div>
      </div>
      {showUploadModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ maxWidth: 720, width: '100%' }}>
            <div className="modal-header">
              <div className="modal-title">Upload Map</div>
              <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowUploadModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <MapUpload
                superAdmin
                tenants={tenants}
                defaultTenantId={selectedTenantId}
                onMapCreated={async () => {
                  setShowUploadModal(false)
                  if (selectedTenantId) await fetchTenantMaps(selectedTenantId)
                }}
              />
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ maxWidth: '95vw', width: '95vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <div className="modal-title">Edit Plan — {editModal.map?.name}</div>
              <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setEditModal(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', gap: 12, height: '100%', overflow: 'auto' }}>
              <div style={{ flex: 1, minWidth: 0, background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <MapEditor
                  map={editModal.map}
                  embedded
                  onMapUpdated={async () => {
                    if (selectedTenantId) await fetchTenantMaps(selectedTenantId)
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


